import { useState, useEffect } from "react";
import { 
  LayoutDashboard, 
  Settings, 
  LogOut,
  User,
  ShieldCheck,
  Stamp,
  Fingerprint,
  Contact2,
  Anchor
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
} from "@/components/ui/sidebar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { supabase } from "@/lib/supabase";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";

const items = [
  { title: "Dashboard", url: "/", icon: LayoutDashboard },
  { title: "Booklet", url: "/booklet", icon: Stamp },
  { title: "Identity", url: "/identity", icon: Fingerprint },
  { title: "Passport", url: "/passport", icon: Contact2 },
];

export function AppSidebar() {
  const [location, setLocation] = useLocation();
  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserEmail(data.user?.email ?? "Unknown Node");
    });
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  return (
    <Sidebar className="border-r border-black/5 bg-white z-[60]"> 
      <SidebarContent className="bg-white pt-[env(safe-area-inset-top)]">
        <SidebarGroup>
          <SidebarGroupLabel className="flex items-center gap-2 px-4 py-8 mb-4">
            <Anchor className="w-5 h-5 text-black" />
            <span className="font-serif text-2xl uppercase tracking-tighter text-black">
              Netport
            </span>
          </SidebarGroupLabel>
          
          <SidebarGroupContent>
            <SidebarMenu className="px-2">
              {items.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton 
                    asChild 
                    isActive={location === item.url}
                    className="hover:bg-black/5 transition-colors h-10 rounded-none mb-1"
                  >
                    <a 
                      href={item.url} 
                      onClick={(e) => {
                        e.preventDefault();
                        setLocation(item.url);
                      }}
                      className="flex items-center gap-3"
                    >
                      <item.icon className={`w-4 h-4 ${location === item.url ? 'text-black' : 'text-black/30'}`} />
                      <span className={`font-serif text-sm uppercase tracking-widest ${location === item.url ? 'font-bold text-black' : 'text-black/60'}`}>
                        {item.title}
                      </span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer handles the iOS home bar area */}
      <SidebarFooter className="p-4 border-t border-black/5 bg-white pb-[calc(env(safe-area-inset-bottom)+1rem)]">
        <SidebarMenu>
          <SidebarMenuItem>
            <Dialog>
              <DialogTrigger asChild>
                <SidebarMenuButton className="text-black/40 hover:text-black transition-colors rounded-none">
                  <Settings className="w-4 h-4 mr-2" />
                  <span className="font-serif text-[10px] uppercase tracking-[0.2em]">Configuration</span>
                </SidebarMenuButton>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px] rounded-none border-black">
                <DialogHeader>
                  <DialogTitle className="text-xl uppercase tracking-widest font-serif">Netport Settings</DialogTitle>
                  <DialogDescription className="font-mono text-[9px] uppercase tracking-widest text-black/40">
                    System Node & Identity Parameters
                  </DialogDescription>
                </DialogHeader>
                
                <div className="py-6 space-y-6">
                  <div className="space-y-2">
                    <label className="text-[9px] text-black/40 uppercase tracking-widest block font-mono">Active Identity</label>
                    <div className="flex items-center gap-3 p-4 bg-zinc-50 border border-black/5">
                      <div className="w-10 h-10 bg-white border border-black/10 flex items-center justify-center">
                        <User className="w-4 h-4 text-black/20" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-mono truncate uppercase text-black/80">{userEmail}</p>
                        <p className="text-[8px] text-emerald-600 flex items-center gap-1 font-mono uppercase font-bold tracking-tighter">
                          <ShieldCheck className="w-3 h-3" /> AAL2 Authenticated
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-black/5">
                    <Button 
                      variant="outline" 
                      className="w-full flex items-center justify-center gap-2 rounded-none border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 uppercase text-[10px] tracking-widest font-serif h-12"
                      onClick={handleLogout}
                    >
                      <LogOut className="w-4 h-4" />
                      Disconnect Session
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}